import { useId } from "react";

import { MAX_CORE_SKILLS } from "@/api/skills";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

export interface CoreSkillToggleProps {
    /** Current value. Controlled only; no internal state. */
    isCore: boolean;
    /** Called with the next value. Hosts are responsible for persistence. */
    onChange: (next: boolean) => void;
    /** Show a spinner and block interaction during an async persist. */
    loading?: boolean;
    /** Block all interaction (e.g., user lacks org-admin role). */
    disabled?: boolean;
    /** Display an error message below the helper text. Sets aria-invalid. */
    error?: string;
    /**
     * Override the id used for the checkbox + label pair. Defaults to a
     * unique id via useId(). Hosts rarely need this.
     */
    id?: string;
    /**
     * Org-wide count of currently-pinned core skills. Renders a soft
     * informational indicator below the helper text. Omit to hide.
     */
    coreCount?: number;
    /**
     * True when this specific skill is already in `coreCount` (edit form).
     * When false, the count represents "other" core skills, useful to
     * distinguish "4 used, this one would make 5" vs "5 used incl. this".
     */
    includesThisSkill?: boolean;
    /**
     * Scope of `coreCount`. "org" counts core skills for a specific tenant;
     * "global" counts globally-visible core skills (no tenant). Controls the
     * copy in the soft indicator ("in this org" vs "globally").
     */
    coreCountScope?: "org" | "global";
    /**
     * Replace the slot-count sentence with fixed copy, keeping the same
     * paragraph so the layout does not shift. Use when a numeric count is
     * meaningless for the current scope.
     */
    coreCountMessage?: string;
    /** Hide the long helper paragraph; keep the label, count, and error. */
    compact?: boolean;
}

const HELPER_COPY =
    "Core skills sit above agent-required and contextual skills in the priority stack, up to the 8-skill cap. Use sparingly; marking too many crowds out everything else.";

function describeCount(
    effectiveCount: number,
    isCore: boolean,
    includesThisSkill: boolean,
    scope: "org" | "global"
): string {
    // Show the cap advisory whenever the user's pending state would hit or
    // exceed the cap, EXCEPT when this skill is already a pinned core skill
    // (no new pressure on the container). The verb tracks whether the user
    // still needs to toggle ("Enabling") or has already toggled ("Saving").
    const pinnedCoreAtCap = isCore && includesThisSkill;
    if (effectiveCount >= MAX_CORE_SKILLS && !pinnedCoreAtCap) {
        const verb = isCore ? "Saving" : "Enabling";
        return `At the ${MAX_CORE_SKILLS}-skill cap. ${verb} drops the oldest from chat containers.`;
    }
    if (isCore && !includesThisSkill) {
        return `${effectiveCount} of ${MAX_CORE_SKILLS} core skill slots used, including this one.`;
    }
    const where = scope === "global" ? "globally" : "in this org";
    return `${effectiveCount} of ${MAX_CORE_SKILLS} core skill slots used ${where}.`;
}

export function CoreSkillToggle({
    isCore,
    onChange,
    loading = false,
    disabled = false,
    error,
    id,
    coreCount,
    includesThisSkill = false,
    coreCountScope = "org",
    coreCountMessage,
    compact = false,
}: CoreSkillToggleProps) {
    const reactId = useId();
    const toggleId = id ?? `core-skill-toggle-${reactId}`;
    const helperId = `${toggleId}-helper`;
    const errorId = `${toggleId}-error`;
    const countId = `${toggleId}-count`;

    // Effective count: what the total would be right now if the user saved.
    // Start from the backend count, add 1 if the user just flipped this
    // skill on (and it wasn't counted), subtract 1 if the user flipped it
    // off (and it was). The backend count reflects the saved state.
    const effectiveCount =
        coreCount == null
            ? null
            : coreCount + (isCore ? 1 : 0) - (includesThisSkill ? 1 : 0);
    const countText =
        coreCountMessage ??
        (effectiveCount == null
            ? null
            : describeCount(
                  effectiveCount,
                  isCore,
                  includesThisSkill,
                  coreCountScope
              ));

    const describedBy = [
        compact ? null : helperId,
        error ? errorId : null,
        countText == null ? null : countId,
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <div className="grid grid-cols-[auto_1fr] items-start gap-x-3 gap-y-1.5">
            <Checkbox
                id={toggleId}
                checked={isCore}
                onCheckedChange={(checked) => onChange(checked === true)}
                disabled={disabled || loading}
                aria-invalid={error ? true : undefined}
                aria-describedby={describedBy}
                className="mt-[2px]"
            />
            <div className="flex items-center gap-2">
                <Label
                    htmlFor={toggleId}
                    className="cursor-pointer text-sm leading-none font-medium"
                >
                    Core skill
                    <span
                        aria-hidden={!isCore}
                        aria-label={isCore ? "Marked as core" : undefined}
                        role="img"
                        className={cn(
                            "bg-brand-copper ml-1 inline-block size-1.5 rounded-full transition-opacity duration-150 ease-out motion-reduce:transition-none",
                            isCore ? "opacity-100" : "opacity-0"
                        )}
                    />
                </Label>
                {loading && (
                    <Spinner className="text-muted-foreground size-3.5" />
                )}
            </div>
            {!compact && (
                <p
                    id={helperId}
                    className="text-muted-foreground col-start-2 max-w-[56ch] text-xs leading-[1.55]"
                >
                    {HELPER_COPY}
                </p>
            )}
            {countText != null && (
                <p
                    id={countId}
                    className="text-muted-foreground col-start-2 text-xs leading-[1.55]"
                >
                    {countText}
                </p>
            )}
            {error && (
                <p
                    id={errorId}
                    role="alert"
                    className="text-destructive col-start-2 text-xs leading-[1.55]"
                >
                    {error}
                </p>
            )}
        </div>
    );
}
